import * as Sentry from '@sentry/react';

// Sentry for the web frontend (org `secretsfl`, project `brandonstrong_frontend`).
// Gated on VITE_SENTRY_DSN: with no DSN, init is a no-op.

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = (import.meta.env.VITE_ENV as string | undefined) || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION as string | undefined;
// Optional first-party tunnel (the API's /v1/tunnel route) to dodge ad
// blockers. Unset: events go straight to Sentry.
const TUNNEL = import.meta.env.VITE_SENTRY_TUNNEL as string | undefined;

const TRACES_SAMPLE_RATE = ENVIRONMENT === 'production' ? 0.2 : 1.0;

/**
 * Known-benign noise, matched against the error message. "Failed to fetch"
 * is deliberately NOT filtered: it is what our own API returns when it is down.
 */
export const IGNORE_ERRORS: (string | RegExp)[] = [
  /ResizeObserver loop (limit exceeded|completed)/,
  /AbortError/,
  'The operation was aborted.',
  'Non-Error promise rejection captured',
  // The Facebook in-app browser injects its own scripts into our page. On iOS
  // they reach for window.webkit.messageHandlers, which is not always there;
  // on Android its navigation logger posts to a Java bridge that is already
  // torn down by the time beforeunload fires. Neither is our code and neither
  // changes anything for the visitor.
  /window\.webkit\.messageHandlers/,
  /Java object is gone/,
  // Collect.js (MobiusPay hosted card fields) leaves a body touch listener
  // behind after the checkout page unmounts its iframes; the next tap on a
  // Safari-based browser trips its SafariFocusFix on the detached iframe.
  /previousFocus\.contentWindow/,
];

/** Scripts whose errors are never ours: browser extensions and the in-app browser bridges. */
export const DENY_URLS: RegExp[] = [
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,
  /^iabjs:\/\//,
];

/** Sentry's own envelope endpoint, derived from the DSN. */
export function ingestUrlFromDsn(dsn: string): string | null {
  try {
    const url = new URL(dsn);
    const projectId = url.pathname.replace(/^\//, '').split('/')[0];
    if (!projectId || !url.username) return null;
    return `${url.protocol}//${url.host}/api/${projectId}/envelope/?sentry_key=${url.username}&sentry_version=7`;
  } catch {
    return null;
  }
}

// Derived from the SDK's own signatures rather than deep-importing its types.
type TransportOptions = Parameters<typeof Sentry.makeFetchTransport>[0];
type Transport = ReturnType<typeof Sentry.makeFetchTransport>;
type TransportRequest = Parameters<Transport['send']>[0];

/**
 * Tunnel first, Sentry direct as the fallback. Routing everything through our
 * API would tie error reporting to the health of the thing most likely to be
 * broken, so when the tunnel fails or answers with an error the same envelope
 * goes straight to Sentry. The envelope carries its own DSN header (the SDK
 * adds it whenever `tunnel` is set), so both routes accept it.
 */
function makeTunnelWithFallback(
  tunnelUrl: string,
  ingestUrl: string,
): (options: TransportOptions) => Transport {
  return (options) => {
    const viaTunnel = Sentry.makeFetchTransport({ ...options, url: tunnelUrl });
    const direct = Sentry.makeFetchTransport({ ...options, url: ingestUrl });
    return {
      send: async (request: TransportRequest) => {
        try {
          const response = await viaTunnel.send(request);
          // The tunnel answers 502 when it cannot reach Sentry itself.
          if (response.statusCode === undefined || response.statusCode < 400) return response;
        } catch {
          // Fall through and try Sentry directly.
        }
        return direct.send(request);
      },
      flush: async (timeout?: number) => {
        const [tunnelled, sent] = await Promise.all([
          viaTunnel.flush(timeout),
          direct.flush(timeout),
        ]);
        return tunnelled && sent;
      },
    };
  };
}

export function initSentry(): void {
  if (!DSN) return;

  // Only swap the transport when both ends of the fallback are known.
  const ingestUrl = TUNNEL ? ingestUrlFromDsn(DSN) : null;
  const transport = TUNNEL && ingestUrl ? makeTunnelWithFallback(TUNNEL, ingestUrl) : undefined;

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    // Keep `tunnel` set even with the custom transport: it makes the SDK put
    // the DSN in the envelope header, which the tunnel route reads.
    tunnel: TUNNEL || undefined,
    transport,
    integrations: [Sentry.browserTracingIntegration()],
    ignoreErrors: IGNORE_ERRORS,
    denyUrls: DENY_URLS,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  void loadReplay();
}

/**
 * Session Replay is loaded after init so its chunk stays out of the entry
 * bundle. It is a nice-to-have: when the chunk does not arrive (a stale tab
 * after a deploy, a dropped connection, or a browser that resolves the import
 * with nothing, which Mobile Safari has done) the page must not report that
 * as an error of its own. The previous `import('@sentry/react')` did exactly
 * that, and being the entry module's own dependency it never split a chunk.
 */
async function loadReplay(): Promise<void> {
  try {
    const mod = await import('./sentryReplay');
    if (typeof mod?.makeReplayIntegration !== 'function') return;
    Sentry.addIntegration(mod.makeReplayIntegration());
  } catch {
    // Replay is optional; the rest of Sentry keeps working without it.
  }
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!DSN) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
